// The arena's fight bot (gate B4): the hero driven one sim step at a time through the same seams a player uses
// (the controller's input and the combat port's `request`, on @rpg/combat since the merge), for the 5-minute seeded bot fight. Each step it reads the
// fight: a telegraphed move about to land on it → dodge (when the stamina allows) and run out of the shape; else the
// nearest enemy → close in (with a seeded weave) and swing when in reach. The fight logs attackers per step; the
// report says how many steps had more attackers than the token cap (the gate: 0).
import { seededRandom } from "@rpg/harness";
import { NO_INPUT, type ControllerInput } from "@rpg/controller";
import type { Runtime } from "@rpg/runtime";
import type { ArenaRig } from "./arena";
import type { Intent, TelegraphShape } from "@rpg/combat";
import type { Vec3 } from "./brains";
import { HERO_ID } from "./fight";

/** A bot fight's report. */
export interface FightReport {
  seed: number;
  difficulty: string;
  simSeconds: number;
  steps: number;
  /** The token cap the fight ran under. */
  cap: number;
  /** The most attackers at once, and the steps with more than the cap (the gate: 0). */
  maxAttackers: number;
  overCap: number;
  /** Steps with 0, 1, 2… attackers. */
  histogram: number[];
  waves: number;
  defeats: Record<string, number>;
  heroDefeats: number;
  heroDamage: number;
  enemyDamage: number;
  dodged: number;
  hits: Record<string, number>;
  /** Moves started, by move id. */
  moves: Record<string, number>;
  tokens: ReturnType<ArenaRig["fight"]["tokens"]["stats"]>;
}

/** Whether a body (centre `p`, radius `r`) touches a telegraphed shape at `origin` facing `yaw`. */
export function inShape(shape: TelegraphShape, origin: readonly number[], yaw: number, p: Vec3, r: number): boolean {
  const dx = p[0] - origin[0]!;
  const dz = p[2] - origin[2]!;
  const d = Math.hypot(dx, dz);
  if (shape.kind === "circle") return d <= shape.radius + r;
  const fx = Math.sin(yaw);
  const fz = Math.cos(yaw);
  if (shape.kind === "cone") {
    if (d > shape.radius + r) return false;
    if (d <= r) return true;
    const cos = (dx * fx + dz * fz) / d;
    const ang = Math.acos(Math.max(-1, Math.min(1, cos)));
    return ang <= shape.angle / 2 + Math.asin(Math.min(1, r / d));
  }
  const along = dx * fx + dz * fz;
  const across = Math.abs(dx * fz - dz * fx);
  return along >= -r && along <= shape.length + r && across <= shape.width / 2 + r;
}

const flat = (a: Vec3, b: Vec3) => Math.hypot(a[0] - b[0], a[2] - b[2]);

/** Install the bot's per-step steering on the rig (until `stop()`). */
export function attachFightBot(rig: ArenaRig, o: { seed?: number; reach?: number; dodgeLead?: number; notice?: number } = {}): { stop(): void } {
  const random = seededRandom(o.seed ?? 20261018);
  const reach = o.reach ?? 2.1;
  const lead = o.dodgeLead ?? 0.28;
  let weave = 0;
  let weaveT = 0;
  // a beat between swings (seeded), so the bot fights like a player, not a metronome
  let swingIn = 0;
  const notice = o.notice ?? 0.7;
  const noticed = new Map<string, { sees: boolean; after: number }>();
  rig.drive = () => {
    const f = rig.fight;
    const s = f.state();
    const h = rig.hero();
    const me: Vec3 = [h.feet.x, h.feet.y, h.feet.z];
    const fighter = f.view(HERO_ID);
    if (!fighter?.alive) {
      rig.heroInput = NO_INPUT;
      rig.heroIntent = null;
      return;
    }
    weaveT -= rig.dt;
    swingIn -= rig.dt;
    if (weaveT <= 0) {
      weave = (random() - 0.5) * 1.2;
      weaveT = 0.6 + random() * 1.2;
    }
    // the threats: telegraphed wind-ups whose shape will cover the hero
    let threat: { away: Vec3; left: number } | null = null;
    for (const e of s.enemies) {
      const st = f.combatState().fighters[e.id];
      if (!e.alive || !st?.alive || !st.move) continue;
      const m = f.combat.moves.get(st.move.id)!;
      if (st.move.step >= m.windup || !m.telegraph) continue;
      const off = m.telegraph.offset ?? 0;
      const base = st.move.anchor ?? e.pos;
      const origin: Vec3 = [base[0] + Math.sin(st.move.yaw) * off, base[1], base[2] + Math.cos(st.move.yaw) * off];
      if (!inShape(m.telegraph.shape, origin, st.move.yaw, me, rig.tuning.hero.radius + 0.5)) continue;
      // like a player, it reads a telegraph after a beat, and misses some (seeded): enemies land hits now and then
      const t = st.move.step * rig.dt;
      const key = `${e.id}:${s.steps - st.move.step}`;
      let seen = noticed.get(key);
      if (!seen) {
        seen = { sees: random() < notice, after: 0.12 + random() * 0.3 };
        noticed.set(key, seen);
        if (noticed.size > 64) noticed.delete(noticed.keys().next().value!);
      }
      if (!seen.sees || t < seen.after) continue;
      const left = (m.windup - st.move.step) * rig.dt;
      if (!threat || left < threat.left) {
        const dx = me[0] - origin[0];
        const dz = me[2] - origin[2];
        const d = Math.hypot(dx, dz) || 1;
        // out of a line sideways, out of a circle or cone straight away
        const away: Vec3 = m.telegraph.shape.kind === "line" ? [Math.cos(st.move.yaw), 0, -Math.sin(st.move.yaw)] : [dx / d, 0, dz / d];
        threat = { away, left };
      }
    }
    let move = { x: 0, z: 0 };
    let sprint = false;
    let intent: Intent | null = null;
    if (threat) {
      move = { x: threat.away[0], z: threat.away[2] };
      sprint = true;
      if (threat.left <= lead && fighter.defending === "none" && fighter.stamina >= rig.content.combat.rules.dodge.cost) intent = { kind: "dodge", dir: threat.away };
    } else {
      // the nearest enemy in the fight
      let best: { pos: Vec3; d: number; r: number } | null = null;
      for (const e of s.enemies) {
        if (!e.alive) continue;
        const d = flat(me, e.pos);
        if (!best || d < best.d) best = { pos: e.pos, d, r: rig.content.kinds[e.kind]!.body.radius };
      }
      if (best) {
        const dx = best.pos[0] - me[0];
        const dz = best.pos[2] - me[2];
        const d = Math.hypot(dx, dz) || 1;
        if (d <= reach + best.r) {
          if (!fighter.move && swingIn <= 0) {
            intent = { kind: "attack", dir: [dx / d, 0, dz / d] };
            swingIn = 0.5 + random() * 0.9;
          }
        } else {
          // close in, weaving a little (seeded)
          const c = Math.cos(weave);
          const sn = Math.sin(weave);
          move = { x: (dx / d) * c - (dz / d) * sn, z: (dx / d) * sn + (dz / d) * c };
          sprint = d > 8;
        }
      } else {
        // between waves: drift back toward the middle
        const d = Math.hypot(me[0], me[2] - 2);
        if (d > 2) move = { x: (-me[0] / d) * 0.5, z: (-(me[2] - 2) / d) * 0.5 };
      }
    }
    const input: ControllerInput = { move, jump: false, jumpPressed: false, sprint, glide: false, glidePressed: false };
    rig.heroInput = input;
    rig.heroIntent = intent;
  };
  return {
    stop() {
      rig.drive = null;
      rig.heroInput = null;
      rig.heroIntent = null;
    },
  };
}

/** Advance `seconds` of sim time without drawing, in one-second slices. */
export function advance(runtime: Runtime, seconds: number): void {
  for (let t = 0; t < seconds - 1e-9; t += 1) runtime.advance(Math.min(1, seconds - t) * 1000, { draw: false });
}

/** A seeded bot fight against the encounter's waves for `minutes` of sim time, logging attackers per step. */
export function runFight(rig: ArenaRig, runtime: Runtime, o: { seed?: number; minutes?: number; difficulty?: string } = {}): FightReport {
  const seed = o.seed ?? 20261018;
  rig.fight.reset({ difficulty: o.difficulty ?? rig.state().difficulty, director: true });
  const log = rig.fight.log;
  log.perStep = [];
  log.caps = [];
  log.logging = true;
  const bot = attachFightBot(rig, { seed });
  const t0 = runtime.time;
  const started0 = rig.state().director?.started ?? 0;
  try {
    // `minutes` of sim time, step by step: a hit-stop (the impact director's, since the merge) freezes the sim while
    // real time runs, so a fixed real-time advance would fall short of the steps asked for
    const want = Math.round(((o.minutes ?? 5) * 60) / rig.dt);
    for (let i = 0; i < want && log.perStep.length < want; i++) runtime.advance(Math.min(1000, (want - log.perStep.length) * rig.dt * 1000 + 0.01), { draw: false });
  } finally {
    bot.stop();
    log.logging = false;
  }
  const per = log.perStep.slice(0, Math.round(((o.minutes ?? 5) * 60) / rig.dt));
  const histogram: number[] = [];
  let overCap = 0;
  let maxAttackers = 0;
  for (let i = 0; i < per.length; i++) {
    const n = per[i]!;
    histogram[n] = (histogram[n] ?? 0) + 1;
    if (n > log.caps[i]!) overCap++;
    maxAttackers = Math.max(maxAttackers, n);
  }
  for (let i = 0; i < histogram.length; i++) histogram[i] ??= 0;
  const t = rig.fight.tally;
  return {
    seed,
    difficulty: rig.state().difficulty,
    simSeconds: runtime.time - t0,
    steps: per.length,
    cap: rig.fight.tokens.cap,
    maxAttackers,
    overCap,
    histogram,
    waves: (rig.state().director?.started ?? 0) - started0,
    defeats: { ...t.defeats },
    heroDefeats: t.heroDefeats,
    heroDamage: Math.round(t.heroDamage),
    enemyDamage: Math.round(t.enemyDamage),
    dodged: t.dodged,
    hits: { ...t.hits },
    moves: { ...t.moves },
    tokens: rig.fight.tokens.stats(),
  };
}
